import { BillingMethodListItem } from 'billing/components/BillingMethodList'
import Button from 'components/Button'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import Spinner from 'components/Spinner'
import { useBrandingSettings } from 'hooks/branding'
import link from 'infra/link'
import SubmissionStatusModalViewModel, {
  SubmissionStatus,
} from 'jobs/creation/SubmissionStatusModalViewModel'
import { observer } from 'mobx-react'
import * as React from 'react'
import { Id } from 'types/definitions'
import { match } from 'utils/pattern-match-util'
import styles from './SubmissionStatusModal.sass'
import { useHistory } from 'react-router'

interface SubmissionStatusModalProps {
  vm: SubmissionStatusModalViewModel
}

/**
 * The SubmissionStatusModal
 */
export default observer(function SubmissionStatusModal({
  vm,
}: SubmissionStatusModalProps) {
  // We can only close the modal if the submission failed.
  // Otherwise the user needs to take an action.
  const canClose = vm.status === SubmissionStatus.Failed

  const history = useHistory()
  const copyJob = React.useCallback(async () => {
    history.push(await vm.copyJob())
  }, [vm.job, history])

  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.close}
      disableCloseOnEsc={!canClose}
      disableCloseOnOutsideClick={!canClose}
    >
      <ModalSheetHeader withCloseButton={canClose} />
      {match(vm.status, {
        [SubmissionStatus.Submitting]: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock dataTest="draft-editor-submission-status-submitting">
                <Illustration status={SubmissionStatus.Submitting} />
                <ModalSheetContentTitle>
                  Please wait, your request is being processed.
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  This may take a few moments.
                </ModalSheetContentDescription>
                <div className={styles.spinner}>
                  <Spinner size={36} />
                </div>
              </ModalSheetContentBlock>
            </ModalSheetContent>
          </>
        ),
        [SubmissionStatus.Failed]: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <Illustration status={SubmissionStatus.Failed} />
                <ModalSheetContentTitle>
                  We were unable to process your request.
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  {vm.error}
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button onClick={vm.close}>Close</Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [SubmissionStatus.Success]: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <Illustration status={SubmissionStatus.Success} />
                <ModalSheetContentTitle>
                  Your request was submitted!
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  Your <strong>{vm.job.displayName}</strong> request has been
                  successfully submitted.
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
              {!vm.canBypassBilling && vm.paymentMethod && (
                <div className={styles.paymentMethod}>
                  <BillingMethodListItem
                    readOnly
                    billingMethod={vm.paymentMethod}
                  />
                </div>
              )}
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterButtonGroup>
                <Button onClick={copyJob}>Create Another</Button>
              </ModalSheetFooterButtonGroup>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button
                  type="primary"
                  to={link.job(Id.assert(vm.job.id))}
                  replace
                  dataTest="draft-editor-submission-status-continue-button"
                >
                  Continue
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
      })}
    </ModalSheet>
  )
})

function Illustration({ status }: { status: SubmissionStatus }) {
  const { corporateMode } = useBrandingSettings()
  return (
    <>
      {!corporateMode && (
        <ModalSheetContentIllustration
          src={match(status, {
            [SubmissionStatus.Submitting]: () =>
              require('assets/images/img_stripe-connecting.svg'),
            [SubmissionStatus.Failed]: () =>
              require('assets/images/img_stripe-error.svg'),
            [SubmissionStatus.Success]: () =>
              require('assets/images/img_stripe-success.svg'),
          })}
        />
      )}
    </>
  )
}
